import { api as viewerApi } from 'v-viewer';
import isEqual from 'lodash/isEqual';
import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import type {
  AddDocumentBodyType,
  WalletDocumentInfosType,
  DisplayExprType,
  GroupedWalletDocumentsType,
  SnackbarMessage
} from '~/types';
import { getDocumentDataQa } from '~/utils/helper';
import 'viewerjs/dist/viewer.css';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import { useWalletDocumentsStore } from '~/stores/wallets/wallet-documents-store';
import { useAddWalletStore } from '~/stores/wallets/add-wallet-store';
import useWalletsApi from '~/composables/api/useWalletsApi';

export const useWalletDocument = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;
  const route = useRoute();
  const { getWalletDocuments, getFile, putDocumentStatus, postDocument } = useWalletsApi();
  const { userInfos, openAddDocumentModal, openEditDocumentModal } = storeToRefs(useWalletsStore());
  const { walletDocuments, documentGroups, selectedDocument, isPdf, selectedType } =
    storeToRefs(useWalletDocumentsStore());
  const { getDisplayExpr, getLookupDataSource, downloadDocument } = useUtils();
  const { src, pdfName, viewPdf } = storeToRefs(usePdfViewerStore());
  const { resetPDFViewerInputs } = usePdfViewerStore();
  const { password } = storeToRefs(useAddWalletStore());
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { retryApiCall } = useUtils();

  const walletIntIdParam = computed(() =>
    Array.isArray(route.params.id)
      ? parseInt(route.params.id[0], 10)
      : parseInt(route.params.id, 10)
  );

  const selectedDocumentGroups = ref([0]);
  const acceptedStatuses = [2];
  const pendingStatuses = [0, 1];
  const rejectedStatuses = [3, 4, 6, 7, 8];
  const imageViewer = ref();

  const addDocumentBody = ref<AddDocumentBodyType>({
    file: undefined,
    wallet: undefined,
    type: undefined
  });

  const handleOpenAddDocumentModal = (type?: number) => {
    selectedType.value = type?.toString();
    openAddDocumentModal.value = !openAddDocumentModal.value;
    if (!openAddDocumentModal.value) {
      addDocumentBody.value = { file: undefined, type: undefined, wallet: undefined };
      selectedType.value = undefined;
    }
  };

  const handleOpenEditDocumentModal = () => {
    openEditDocumentModal.value = !openEditDocumentModal.value;
  };

  const submitAddDocument = async (emit: Function) => {
    if (!addDocumentBody.value.file || !addDocumentBody.value.type) return;
    try {
      const formData = new FormData();
      formData.append('file', addDocumentBody.value.file.file, addDocumentBody.value.file.name);
      formData.append('wallet', walletIntIdParam.value.toString());
      formData.append('type', addDocumentBody.value.type);
      const response = await postDocument(formData);
      localStorage.setItem(
        'savedDocument',
        JSON.stringify({ group: Number(addDocumentBody.value.type), Id: Number(response?.Id) })
      );
      localStorage.setItem('selectedDocumentId', JSON.stringify(Number(response?.Id)));
      handleOpenAddDocumentModal();
      add({
        type: 'success',
        message: t('wallets.details.documents.add.successAdd', { id: response?.Id })
      } as SnackbarMessage);
      emit();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const resetDocumentValues = () => {
    walletDocuments.value = {};
    documentGroups.value = [];
  };

  const fetchDocuments = async (): Promise<WalletDocumentInfosType[]> => {
    const documents = await getWalletDocuments();
    documents.sort((a, b) => a.relevant_document - b.relevant_document);
    return documents;
  };

  const groupDocuments = (documents: WalletDocumentInfosType[]): GroupedWalletDocumentsType => {
    const groupedDocuments: GroupedWalletDocumentsType = [];
    documents.forEach((document) => {
      if (!groupedDocuments[document.relevant_document]) {
        groupedDocuments[document.relevant_document] = [];
        documentGroups.value.push(createDocumentGroup(document));
      }
      groupedDocuments[document.relevant_document].push(document);
    });
    return groupedDocuments;
  };

  const createDocumentGroup = (document: WalletDocumentInfosType) => ({
    title: `${document.relevant_document} - ${getDisplayExpr('documentTypes', document.relevant_document)}`,
    value: document.relevant_document,
    dataQa: getDocumentDataQa(document.relevant_document)
  });

  const getStoredDocumentDetails = () => {
    const indexGroup = Number(localStorage.getItem('selectedDocumentGroupID')) ?? null;
    const documentId = Number(localStorage.getItem('selectedDocumentId')) ?? null;
    return { indexGroup, documentId };
  };

  const loadWalletDocuments = async () => {
    try {
      resetDocumentValues();
      const documents: WalletDocumentInfosType[] = await fetchDocuments();
      if (documents.length > 0) {
        const groupedDocuments: GroupedWalletDocumentsType = groupDocuments(documents);
        const savedDocument = localStorage.getItem('savedDocument');
        let { indexGroup, documentId } = getStoredDocumentDetails();
        if (savedDocument) {
          const documentSaved = savedDocument ? JSON.parse(savedDocument) : null;
          const index =
            documentGroups.value.findIndex((item) => isEqual(item.value, documentSaved?.group)) ??
            null;
          indexGroup = index || 0;
          documentId = documentSaved?.Id ?? null;
          selectedDocumentGroups.value = [indexGroup];
          localStorage.setItem('selectedDocumentGroupID', JSON.stringify(index));
          localStorage.removeItem('savedDocument');
        }
        walletDocuments.value = groupedDocuments;
        selectedDocument.value =
          walletDocuments.value[documentGroups.value[indexGroup].value].find((item) =>
            isEqual(item.id, documentId)
          ) || walletDocuments.value[documentGroups.value[0].value][0];
      }
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const documentArgs = (documentData: WalletDocumentInfosType) => ({
    title: documentData.fileName,
    hasIcon: true,
    iconOnly: true,
    theme: 'ghost',
    icon: documentData.id === selectedDocument.value?.id ? 'InsertDriveFile' : 'InsertFileOutline',
    subtitles: [
      `${t('wallets.details.documents.list.receivedOn')}: ${getFormattedDate(documentData.date_sent, 'L LTS', locale.value)}`,
      `Id: ${documentData.id}`
    ]
  });

  const isDocumentGroupActive = (index: number) => selectedDocumentGroups.value.includes(index);

  const updateSelectedDocumentGroup = (index: number) => {
    selectedDocumentGroups.value = selectedDocumentGroups.value.includes(index) ? [] : [index];
    localStorage.setItem('selectedDocumentGroupID', String(index));
  };

  const handleWalletTypes = (
    documentType: number,
    statusType: 'pending' | 'accepted' | 'rejected'
  ) => {
    switch (statusType) {
      case 'pending':
        return walletDocuments.value[documentType].filter((document) =>
          pendingStatuses.includes(document.document_status)
        ).length;
      case 'rejected':
        return walletDocuments.value[documentType].filter((document) =>
          rejectedStatuses.includes(document.document_status)
        ).length;
      default:
        return walletDocuments.value[documentType].filter((document) =>
          acceptedStatuses.includes(document.document_status)
        ).length;
    }
  };

  const handleDocumentStatusChip = (status: number) => {
    const found = getLookupDataSource('documentStatuses').find(
      (element: DisplayExprType) => element.value === status
    );
    const subStatus = found.text.split(':');
    return {
      ...found,
      text: subStatus[0],
      substatuses: subStatus.length > 1 ? subStatus.slice(1) : []
    };
  };

  const getDocument = async () => {
    try {
      resetPDFViewerInputs();
      const response = await retryApiCall(getFile, 5, 3000);
      isPdf.value = selectedDocument.value?.path.split('.').pop()?.toLowerCase() === 'pdf';
      src.value = URL.createObjectURL(
        new Blob([response], isPdf.value ? { type: 'application/pdf' } : undefined)
      );
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const handlePreview = () => {
    if (selectedDocument.value) {
      if (isPdf.value) {
        pdfName.value = selectedDocument.value.fileName;
        viewPdf.value = true;
      } else {
        viewerApi({
          images: [src.value],
          options: {
            button: false,
            navbar: false,
            title: false,
            fullscreen: false,
            toolbar: {
              zoomIn: true,
              zoomOut: true,
              oneToOne: true,
              reset: true,
              play: true,
              rotateLeft: true,
              rotateRight: true,
              flipHorizontal: true,
              flipVertical: true
            }
          }
        });
      }
    }
  };

  const handleOpenNewTab = () => {
    if (isPdf.value) {
      window.open(src.value, '_blank');
    } else {
      const image = new Image();
      image.src = src.value;
      const imageWindow = window.open('');
      imageWindow?.document.write(image.outerHTML);
      imageWindow?.document.close();
    }
  };

  const handleDownloadDocument = () => {
    if (selectedDocument.value) {
      downloadDocument(src.value, selectedDocument.value.fileName);
    }
  };

  const handleOpenUpdateDocument = () => {
    if (selectedDocument.value) {
      handleOpenAddDocumentModal(selectedDocument.value?.relevant_document);
    }
  };

  const submitEditDocumentStatus = async (status: string, comment: string) => {
    if (!password.value) return;
    try {
      const body = {
        Wallet: userInfos.value.wallet,
        Language: locale?.value,
        Password: password.value?.value,
        Status: status,
        Comment: comment
      };
      const data = await putDocumentStatus(body);
      const value = data.value as { Id: number };
      add({
        type: 'success',
        message: t('wallets.details.documents.view.successUpdate', { id: value.Id })
      } as SnackbarMessage);
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  return {
    addDocumentBody,
    handleOpenAddDocumentModal,
    handleOpenEditDocumentModal,
    submitAddDocument,
    submitEditDocumentStatus,
    isDocumentGroupActive,
    loadWalletDocuments,
    documentArgs,
    updateSelectedDocumentGroup,
    handleWalletTypes,
    handleDocumentStatusChip,
    getDocument,
    handlePreview,
    handleOpenNewTab,
    handleDownloadDocument,
    handleOpenUpdateDocument,
    imageViewer
  };
};
